import React, { useState } from "react";
import { X, Power, Gauge, Package, Plane } from "lucide-react";
import { ASSETS } from "../../lib/api";
import { Flag, fmtFans } from "../../lib/tour";
import { PurchaseBurst } from "./TourUpgradesModal";
import { ScreenPortal } from "../MobileZoom";

const mmss = (s) => `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, "0")}`;

function StatRow({ icon: Icon, label, value, next, level, max, cost, hotDogs, onUpgrade, testId }) {
  const maxed = cost == null;
  const [burst, setBurst] = useState(0);
  const [busy, setBusy] = useState(false);
  const buy = async () => {
    setBusy(true);
    const ok = await onUpgrade();
    setBusy(false);
    if (ok) { setBurst((b) => b + 1); setTimeout(() => setBurst(0), 1200); }
  };
  return (
    <div className={`relative overflow-hidden rounded-lg p-3 bg-[#0A051A] border transition-[transform,border-color,box-shadow] duration-300 ${burst ? "scale-[1.04] border-[#39FF14] shadow-[0_0_18px_#39FF1466]" : "border-[#2D1C52]"}`} data-testid={`tour-plane-stat-${testId}`}>
      {burst > 0 && <PurchaseBurst key={burst} color={maxed ? "#FFD700" : "#39FF14"} />}
      <div className="flex items-center gap-2">
        <Icon size={16} className="text-[#00F0FF]" />
        <span className="font-heading uppercase tracking-wider text-sm">{label}</span>
        <span key={level} className={`ml-auto font-mono text-[10px] ${burst ? "text-[#39FF14] tour-lv-pop" : "text-[#A89BCC]"}`} data-testid={`tour-plane-level-${testId}`}>Lv {level}/{max}</span>
      </div>
      <div className="mt-1 font-mono text-xs text-white">
        {value}{!maxed && <span className="text-[#39FF14]"> → {next}</span>}
      </div>
      <button data-testid={`tour-plane-upgrade-${testId}`} disabled={maxed || busy || hotDogs < cost} onClick={buy}
              className="mt-2 w-full btn-neon text-xs py-1.5 flex items-center justify-center gap-1.5 disabled:opacity-40 active:scale-95 transition-transform">
        {maxed ? "Maxed" : <><img src={ASSETS.currency.hotdog} alt="" className="w-4 h-4" /> {cost.toLocaleString()}</>}
      </button>
    </div>
  );
}

export function TourPlaneModal({ plane, state, onClose, onUpgrade, onToggle, onRoute }) {
  const venues = state.locations.filter((l) => !l.daily);
  const taken = new Set(state.planes.filter((p) => p.id !== plane.id).map((p) => p.location_id));
  return (<ScreenPortal>
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4" data-testid="tour-plane-modal"
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="card-surface w-full max-w-md p-5 border-2 border-[#00F0FF]/60 shadow-[0_0_40px_#00F0FF33] pw-pop">
        <div className="flex items-center gap-3 mb-4">
          <img src="/tour/World Tour Icon Plane.webp" alt="" className="w-10 h-10" style={{ filter: plane.enabled ? "drop-shadow(0 0 8px #00F0FF)" : "grayscale(1)" }} />
          <div>
            <h2 className="font-heading text-2xl uppercase neon-cyan leading-none">Airplane {plane.n}</h2>
            <div className="font-mono text-[10px] text-[#A89BCC] mt-1 flex items-center gap-1">
              <img src={ASSETS.currency.hotdog} alt="" className="w-3.5 h-3.5" /> {state.hot_dogs.toLocaleString()} Hot Dogs
            </div>
          </div>
          <button data-testid="tour-plane-toggle" onClick={onToggle}
                  className={`ml-auto px-3 py-1.5 rounded-full border-2 font-heading text-xs uppercase tracking-widest flex items-center gap-1.5 transition-colors ${plane.enabled ? "border-[#39FF14] text-[#39FF14] bg-[#39FF14]/10" : "border-[#695D8E] text-[#695D8E]"}`}>
            <Power size={13} /> {plane.enabled ? "On" : "Off"}
          </button>
          <button data-testid="tour-plane-close" onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10"><X size={18} /></button>
        </div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#A89BCC] mb-1.5">Route: Airport ⇄</div>
        <div className="grid grid-cols-3 gap-1.5 mb-4" data-testid="tour-plane-routes">
          {venues.map((v) => {
            const mine = plane.location_id === v.id;
            return (
              <button key={v.id} data-testid={`tour-plane-route-${v.id}`} disabled={taken.has(v.id)} onClick={() => !mine && onRoute(v.id)}
                      className={`rounded-md px-1.5 py-1.5 border text-[9px] font-heading uppercase leading-tight flex flex-col items-center gap-0.5 transition-colors disabled:opacity-30 ${mine ? "border-[#FFE600] text-[#FFE600] bg-[#FFE600]/10 shadow-[0_0_10px_#FFE60055]" : "border-[#2D1C52] hover:border-[#00F0FF]"}`}>
                <Flag c={v.currency} className="w-5 h-3.5" /> {v.name}
              </button>
            );
          })}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <StatRow icon={Gauge} label="Speed" testId="speed" value={`Flight ${mmss(plane.flight_time)}`} next={mmss(plane.next_flight_time)}
                   level={plane.speed_level} max={plane.speed_max} cost={plane.speed_cost} hotDogs={state.hot_dogs} onUpgrade={() => onUpgrade("speed")} />
          <StatRow icon={Package} label="Capacity" testId="capacity" value={`${fmtFans(plane.capacity)} fans`} next={fmtFans(plane.next_capacity)}
                   level={plane.capacity_level} max={plane.capacity_max} cost={plane.capacity_cost} hotDogs={state.hot_dogs} onUpgrade={() => onUpgrade("capacity")} />
        </div>
        <div className="mt-3 font-mono text-[10px] text-[#A89BCC] flex items-center gap-1.5">
          <Plane size={12} className="text-[#00F0FF]" /> Flies out and back once per flight time and restocks its venue at the turnaround. Only flies while the Tour screen is open.
        </div>
      </div>
    </div>
  </ScreenPortal>);
}
